import { yn } from "@-/com.npmjs.yn";

export type SuperwallTestModeBehavior = "always" | "automatic" | "whenEnabledForUser";

export type SuperwallTestModeFallbackProduct = Readonly<{
  amount: number;
  currency: string;
  entitlementIdentifier: string;
  identifier: string;
  name: string;
  period: "month" | "year";
  periodCount: number;
}>;

const STAGING_QA_FALLBACK_PRODUCTS = [
  {
    amount: 5999,
    currency: "USD",
    entitlementIdentifier: "premium",
    identifier: "haniverse_staging_premium_annual",
    name: "annual",
    period: "year",
    periodCount: 1,
  },
  {
    amount: 1299,
    currency: "USD",
    entitlementIdentifier: "premium",
    identifier: "haniverse_staging_premium_monthly",
    name: "monthly",
    period: "month",
    periodCount: 1,
  },
] as const satisfies readonly SuperwallTestModeFallbackProduct[];

/**
 * Local product metadata for the SDK's simulated checkout when its test-product endpoint is empty.
 *
 * The native bridge waits for Superwall's own test-product load first, and uses this only when that
 * load completes with no products. These values never reach an ordinary staging or production
 * launch, and they do not grant an entitlement; the learner still has to confirm the native SDK
 * purchase drawer.
 */
export function getSuperwallTestModeFallbackProducts({
  appVariant,
  simulatePurchases,
}: {
  appVariant: string | undefined;
  simulatePurchases: boolean;
}): readonly SuperwallTestModeFallbackProduct[] | undefined {
  return appVariant === "staging" && simulatePurchases
    ? STAGING_QA_FALLBACK_PRODUCTS
    : undefined;
}

/**
 * Automatic paywall preloading must wait until the native staging-QA fallback products exist.
 * Ordinary launches keep the SDK default; only the explicit simulated-purchase launch disables it.
 */
export function getSuperwallTestModePaywallOptions({
  appVariant,
  simulatePurchases,
}: {
  appVariant: string | undefined;
  simulatePurchases: boolean;
}): Readonly<{ shouldPreload: false }> | undefined {
  return appVariant === "staging" && simulatePurchases ? { shouldPreload: false } : undefined;
}

/**
 * Which Superwall test-mode behavior a build configures with.
 *
 * Local, development and alpha builds share the staging Superwall app (bundle
 * `dev.improve.haniverse.staging`), so `automatic` would flip them into test mode on the bundle-ID
 * mismatch — and test mode presents
 * a blocking configuration modal on EVERY cold launch, right over Maestro flows and agent QA. There
 * they only enter test mode for users marked as test store users in the staging project, or for
 * everyone when a developer opts in with `EXPO_PUBLIC_SUPERWALL_FORCE_TEST_MODE` in their own
 * `.env.local` — the simulator cannot load App Store products, so test mode is the only way to
 * exercise the paywall locally.
 *
 * A development bundle (`isDevelopmentBundle`, i.e. `__DEV__`) counts as local whatever its variant
 * says: the variant is inlined from the Metro process's `EXPO_PUBLIC_APP_VARIANT`, which the dev
 * stack never sets, and an unset variant resolves to production — which is what put every
 * dev-client launch into test mode.
 *
 * Staging and production release builds normally get `automatic`, whatever the env flag says: their
 * bundle IDs match their Superwall app, so test mode never activates there except for
 * dashboard-marked test users, and a stray flag must never turn real (sandbox or paid) purchases
 * into simulated ones. Staging alone also accepts an explicit `hanSimulatePurchases` launch
 * argument for repeatable end-to-end tests. It opens the SDK's simulated purchase drawer instead
 * of StoreKit; it does not grant Premium before the learner completes the purchase.
 */
export function getSuperwallTestModeBehavior({
  appVariant,
  forceTestMode,
  isDevelopmentBundle = false,
  simulatePurchases = false,
}: {
  appVariant: string | undefined;
  forceTestMode: string | undefined;
  isDevelopmentBundle?: boolean;
  simulatePurchases?: boolean;
}): SuperwallTestModeBehavior {
  if (appVariant === "staging" && simulatePurchases) {
    return "always";
  }
  if (
    !isDevelopmentBundle &&
    appVariant !== "local" &&
    appVariant !== "development" &&
    appVariant !== "alpha"
  ) {
    return "automatic";
  }
  return yn(forceTestMode, { default: false }) ? "always" : "whenEnabledForUser";
}
